import { bannerConfig } from "../config/bannerConfig";

/**
 * Serviço para gerenciar banners, incluindo filtros por cliente e controle de estado
 */
export const bannerService = {
  /**
   * Obter banners para um cliente específico
   * @param {string} clienteTenant - Identificador do tenant/cliente
   * @returns {Array} Lista de banners válidos para o cliente
   */
  async getBannersForClient(clienteTenant = "default") {
    try {
      const allBanners = bannerConfig.getBannersForClient(clienteTenant);
      const validBanners = this.filterValidBanners(allBanners);

      // Ordenar por prioridade (menor número = aparece primeiro)
      return validBanners.sort((a, b) => (a.priority || 0) - (b.priority || 0));
    } catch (error) {
      console.error(
        "Erro ao obter banners para cliente:",
        clienteTenant,
        error
      );
      return [];
    }
  },

  /**
   * Filtrar banners válidos baseado em datas e condições
   * @param {Array} banners - Lista de banners para filtrar
   * @returns {Array} Lista de banners válidos
   */
  filterValidBanners(banners) {
    const now = new Date();

    return banners.filter((banner) => {
      // Verificar se o banner está ativo
      if (!banner.active) {
        return false;
      }

      // Verificar data de início
      if (banner.startDate) {
        const startDate = new Date(banner.startDate);
        if (now < startDate) {
          return false;
        }
      }

      // Verificar data de fim
      if (banner.endDate) {
        const endDate = new Date(banner.endDate);
        if (now > endDate) {
          return false;
        }
      }

      // Verificar se a imagem existe (validação básica)
      if (!banner.image) {
        return false;
      }

      return true;
    });
  },

  /**
   * Verificar se um banner foi fechado pelo usuário
   * @param {string} bannerId - ID do banner
   * @param {string} bannerVersion - Versão do banner
   * @returns {boolean} True se o banner foi fechado
   */
  isBannerClosed(bannerId, bannerVersion) {
    try {
      const storageKey = this.getStorageKey(bannerId, bannerVersion);
      const closedBanners = this.getClosedBanners();
      return closedBanners.includes(storageKey);
    } catch (error) {
      console.error("Erro ao verificar status do banner:", error);
      return false;
    }
  },

  /**
   * Marcar um banner como fechado
   * @param {string} bannerId - ID do banner
   * @param {string} bannerVersion - Versão do banner
   */
  closeBanner(bannerId, bannerVersion) {
    try {
      const storageKey = this.getStorageKey(bannerId, bannerVersion);
      const closedBanners = this.getClosedBanners();

      if (!closedBanners.includes(storageKey)) {
        closedBanners.push(storageKey);
        this.saveClosedBanners(closedBanners);
      }
    } catch (error) {
      console.error("Erro ao fechar banner:", error);
    }
  },

  /**
   * Reabrir um banner específico (para debug/admin)
   * @param {string} bannerId - ID do banner
   * @param {string} bannerVersion - Versão do banner
   */
  reopenBanner(bannerId, bannerVersion) {
    try {
      const storageKey = this.getStorageKey(bannerId, bannerVersion);
      const closedBanners = this.getClosedBanners();
      const index = closedBanners.indexOf(storageKey);

      if (index > -1) {
        closedBanners.splice(index, 1);
        this.saveClosedBanners(closedBanners);
      }
    } catch (error) {
      console.error("Erro ao reabrir banner:", error);
    }
  },

  /**
   * Limpar todos os banners fechados (para debug/admin)
   */
  clearAllClosedBanners() {
    try {
      localStorage.removeItem(this.getStoragePrefix());
    } catch (error) {
      console.error("Erro ao limpar banners fechados:", error);
    }
  },

  /**
   * Obter estatísticas dos banners
   * @param {string} clienteTenant - Identificador do tenant/cliente
   * @returns {Object} Estatísticas dos banners
   */
  async getBannerStats(clienteTenant = "default") {
    try {
      const allBanners = bannerConfig.getBannersForClient(clienteTenant);
      const validBanners = this.filterValidBanners(allBanners);

      return {
        total: allBanners.length,
        valid: validBanners.length,
        closed: validBanners.filter((banner) =>
          this.isBannerClosed(banner.id, banner.version)
        ).length,
        visible: validBanners.filter(
          (banner) => !this.isBannerClosed(banner.id, banner.version)
        ).length,
      };
    } catch (error) {
      console.error("Erro ao obter estatísticas dos banners:", error);
      return {
        total: 0,
        valid: 0,
        closed: 0,
        visible: 0,
      };
    }
  },

  // === Métodos privados ===

  /**
   * Gerar chave de storage para um banner
   * @param {string} bannerId - ID do banner
   * @param {string} bannerVersion - Versão do banner
   * @returns {string} Chave para localStorage
   */
  getStorageKey(bannerId, bannerVersion) {
    return `${bannerId}:${bannerVersion || "1.0"}`;
  },

  /**
   * Obter prefixo das chaves de storage
   * @returns {string} Prefixo para localStorage
   */
  getStoragePrefix() {
    return "multpex_closed_banners";
  },

  /**
   * Obter lista de banners fechados do localStorage
   * @returns {Array} Lista de banners fechados
   */
  getClosedBanners() {
    try {
      const stored = localStorage.getItem(this.getStoragePrefix());
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Erro ao ler banners fechados:", error);
      return [];
    }
  },

  /**
   * Salvar lista de banners fechados no localStorage
   * @param {Array} closedBanners - Lista de banners fechados
   */
  saveClosedBanners(closedBanners) {
    try {
      localStorage.setItem(
        this.getStoragePrefix(),
        JSON.stringify(closedBanners)
      );
    } catch (error) {
      console.error("Erro ao salvar banners fechados:", error);
    }
  },

  /**
   * Validar configuração de um banner
   * @param {Object} banner - Configuração do banner
   * @returns {boolean} True se a configuração é válida
   */
  validateBanner(banner) {
    if (!banner || typeof banner !== "object") {
      return false;
    }

    // Campos obrigatórios
    const requiredFields = ["id", "title", "image"];
    for (const field of requiredFields) {
      if (!banner[field]) {
        console.warn(`Banner inválido: campo '${field}' é obrigatório`, banner);
        return false;
      }
    }

    // Validar versão
    if (banner.version && typeof banner.version !== "string") {
      console.warn("Banner inválido: versão deve ser uma string", banner);
      return false;
    }

    // Validar datas
    if (banner.startDate && isNaN(new Date(banner.startDate).getTime())) {
      console.warn(
        "Banner inválido: startDate deve ser uma data válida",
        banner
      );
      return false;
    }

    if (banner.endDate && isNaN(new Date(banner.endDate).getTime())) {
      console.warn("Banner inválido: endDate deve ser uma data válida", banner);
      return false;
    }

    // Validar botão de ação
    if (
      banner.buttonAction &&
      (!banner.buttonAction.type || !banner.buttonAction.value)
    ) {
      console.warn(
        "Banner inválido: buttonAction deve ter type e value",
        banner
      );
      return false;
    }

    return true;
  },

  /**
   * Debug: Listar todos os banners fechados
   * @returns {Array} Lista de banners fechados com informações
   */
  debugListClosedBanners() {
    const closedBanners = this.getClosedBanners();
    return closedBanners.map((key) => {
      const [id, version] = key.split(":");
      return { id, version, key };
    });
  },
};

// Exportar também como default para compatibilidade
export default bannerService;
